import React, { useState, useEffect } from 'react';
import { X, Upload, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { getAuthHeader } from '@/utils/api';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

export const SingleImageUploader = ({ imageUrl = '', onChange, category = 'news' }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(imageUrl);

  useEffect(() => {
    setPreview(imageUrl);
  }, [imageUrl]);

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error(`Type de fichier non autorisé. Formats acceptés : JPG, PNG, WebP`);
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(`Fichier trop volumineux. Taille maximale : 5 MB`);
      return false;
    }

    return true;
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    
    if (!file) return;

    if (!validateFile(file)) {
      event.target.value = '';
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);

      const response = await axios.post(
        `${BACKEND_URL}/api/upload-image`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        const newImageUrl = response.data.image_url;
        setPreview(newImageUrl);
        onChange(newImageUrl);
        toast.success('Image téléversée avec succès');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du téléversement');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleRemove = async () => {
    if (!preview) return;

    try {
      // Only delete if it's a local uploaded image
      if (preview.startsWith('/uploads/')) {
        await axios.delete(`${BACKEND_URL}/api/delete-image`, {
          params: { image_url: preview },
          headers: getAuthHeader(),
        });
      }

      setPreview('');
      onChange('');
      toast.success('Image supprimée');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-slate-700">
          Image de couverture
        </label>
        {!preview && (
          <label htmlFor="news-image-upload" className="cursor-pointer">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-[#127abb] text-[#127abb] hover:bg-primary-50"
              disabled={uploading}
              onClick={() => document.getElementById('news-image-upload').click()}
            >
              <Upload size={16} className="mr-2" />
              {uploading ? 'Téléversement...' : 'Téléverser'}
            </Button>
            <input
              id="news-image-upload"
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              className="hidden"
              onChange={handleFileSelect}
              disabled={uploading}
            />
          </label>
        )}
      </div>

      {preview ? (
        <div className="relative group aspect-video bg-slate-100 rounded-lg overflow-hidden border-2 border-slate-200 hover:border-[#127abb] transition-colors max-w-md">
          <img
            src={preview.startsWith('/') ? `${BACKEND_URL}${preview}` : preview}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = '/images/placeholder.jpg';
            }}
          />
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
          >
            <X size={16} />
          </button>
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
            <label htmlFor="news-image-replace" className="cursor-pointer">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="text-xs"
                disabled={uploading}
                onClick={() => document.getElementById('news-image-replace').click()}
              >
                {uploading ? 'Téléversement...' : 'Remplacer'}
              </Button>
              <input
                id="news-image-replace"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                className="hidden"
                onChange={handleFileSelect}
                disabled={uploading}
              />
            </label>
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center max-w-md">
          <ImageIcon className="mx-auto h-10 w-10 text-slate-400 mb-2" />
          <p className="text-sm text-slate-600 mb-1">Aucune image</p>
          <p className="text-xs text-slate-500">
            JPG, PNG, WebP (max 5 MB)
          </p>
        </div>
      )}
    </div>
  );
};

export default SingleImageUploader;
